import * as React from "react";
import _ from "lodash";
import {
  createStyles,
  Paper,
  Theme,
  Fab,
  makeStyles,
  IconButton,
  TextField,
  Grid,
  FormControlLabel,
  MenuItem,
  Checkbox,
} from "@material-ui/core";
import {
  Save as SaveIcon,
  ArrowBack as BackIcon,
  FilterList as FilterIcon,
} from "@material-ui/icons";
import { AnvokError, Form } from "lib";
import useReactRouter from "use-react-router";
import EditRow from "./douanes/EditRow";
import LoaderInline from "./components/LoaderInline";
import { ItemValue, calcTotal } from "./douanes/Douanes";
import { useErrorHandler } from "../ErrorHandler";
import IconMenu from "./components/IconMenu";
import { useApiClient } from "lib/store";
import { ApiDouane, ApiItem, ApiDouaneTypeLabel } from "lib/api";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: "100%",
      overflowX: "auto",
      marginBottom: 80,
      overflow: "auto",
    },
    header: {
      padding: theme.spacing(1),
      borderBottom: "1px solid #e0e0e0",
    },
    ref: {
      fontFamily: '"Courier New", monospace',
      color: "rgb(90,90,90)",
    },
    fab: {
      position: "fixed",
      bottom: theme.spacing(2),
      right: theme.spacing(2),
    },
    labelField: {
      width: 400,
    },
  })
);

interface Params {
  id?: string;
}

type ValuesType = { [k: string]: ItemValue[] };

const DouaneEdit: React.FunctionComponent = () => {
  const api = useApiClient();
  const [loading, setLoading] = React.useState("Chargement des données...");
  const [inventory, setInventory] = React.useState<ApiDouane>();
  const [values, setValues] = React.useState<ValuesType>();
  const [label, setLabel] = React.useState("");
  const [items, setItems] = React.useState<{ [k: string]: ApiItem }>({});
  const [showZero, setShowZero] = React.useState(false);
  const [showDisabled, setShowDisabled] = React.useState(false);
  const { match } = useReactRouter<Params>();
  const classes = useStyles();
  const { history } = useReactRouter();
  const errorHandler = useErrorHandler();

  React.useEffect(() => {
    setLoading("Chargement des données...");
    api
      .GET("/api/douanes/{id}", { params: { path: { id: match.params.id! } } })
      .then((r) => {
        if (r.error) {
          throw new AnvokError(r.error.message);
        }
        if (!r.data) {
          throw new AnvokError("Inventaire introuvable");
        }
        const inventory = r.data;
        const v = inventory.items.reduce<ValuesType>((prev, cur) => {
          prev[cur.id] = cur.packagings.map((v) => {
            return {
              label: v.label,
              count: v.count === null || v.count === undefined ? "" : v.count,
              multiplier: v.multiplier,
            };
          });
          return prev;
        }, {});
        setValues(v);
        setInventory(inventory);
        setLabel(inventory.label);
        return api.GET("/api/items").then((r) => {
          if (r.error) {
            throw new AnvokError(r.error.message);
          }
          setItems(_.keyBy(r.data.items, "_id"));
        });
      })
      .catch(errorHandler)
      .finally(() => {
        setLoading("");
      });
  }, [api, match.params.id, errorHandler]);

  const handleChange = React.useCallback(
    (id: string, index: number, value: number | string) => {
      setValues((c) => {
        if (c) {
          const data = c[id];
          data[index].count = value;
          return { ...c, [id]: [...data] };
        }
      });
    },
    []
  );

  function handleSave() {
    if (inventory && values) {
      setLoading("Enregistrement en cours...");

      const items = inventory.items.map((i) => {
        const itemId = i.id;
        const packagings = i.packagings.map((p, idx) => {
          if (values[itemId] && values[itemId][idx]) {
            const count = values[itemId][idx].count;
            return {
              ...p,
              count: typeof count == "string" ? undefined : count,
            };
          }
          return p;
        });
        return {
          ...i,
          packagings,
          stock: calcTotal(values[itemId], inventory.type, i.initialStock),
        };
      });

      const newlabel = label.trim().length > 0 ? label : inventory.label;

      api
        .PUT("/api/douanes/{id}", {
          params: { path: { id: inventory._id } },
          body: { label: newlabel, items },
        })
        .then((r) => {
          if (r.error) {
            throw new AnvokError(r.error.message);
          }
          history.push("/douanes");
        })
        .catch(errorHandler)
        .finally(() => {
          setLoading("");
        });
    }
  }

  const readonly = inventory ? inventory.next !== null : true;

  return (
    <Paper className={classes.root} elevation={1}>
      <Grid
        justify="space-between"
        container
        className={classes.header}
        spacing={0}
      >
        <Grid item>
          <IconButton size="small" onClick={() => history.push("/douanes")}>
            <BackIcon />
          </IconButton>
          {inventory && (
            <span>
              <TextField
                value={label}
                className={classes.labelField}
                onChange={Form.handleChange(setLabel)}
                InputProps={{
                  readOnly: readonly,
                  disabled: readonly,
                }}
              />
              <small>({ApiDouaneTypeLabel.get(inventory.type)})</small>
            </span>
          )}
        </Grid>
        <Grid item>
          <IconMenu icon={<FilterIcon />}>
            <MenuItem>
              <FormControlLabel
                control={
                  <Checkbox
                    onChange={Form.handleChangeCheck(setShowZero)}
                    checked={showZero}
                  />
                }
                label="Articles stock 0"
              />
            </MenuItem>
            <MenuItem>
              <FormControlLabel
                control={
                  <Checkbox
                    onChange={Form.handleChangeCheck(setShowDisabled)}
                    checked={showDisabled}
                  />
                }
                label="Articles désactivés"
              />
            </MenuItem>
          </IconMenu>
        </Grid>
      </Grid>
      <LoaderInline show={Boolean(loading)} message={loading} />
      {inventory &&
        values &&
        !Boolean(loading) &&
        inventory.items.map((i) => {
          const id = i.id;
          if (!showZero && i.initialStock + i.stock === 0) {
            return null;
          }
          if (!showDisabled && items[id] && items[id].disabled) {
            return null;
          }
          return (
            <EditRow
              key={id}
              item={i}
              type={inventory.type}
              value={values[id]}
              readonly={readonly}
              onChange={handleChange}
            />
          );
        })}

      {inventory && inventory.next === null && (
        <Fab color="secondary" className={classes.fab} onClick={handleSave}>
          <SaveIcon />
        </Fab>
      )}
    </Paper>
  );
};

export default DouaneEdit;
